/**
 * How a change in the cart announces itself.
 *
 * One grammar, three verbs, chosen by *what changed* rather than by which
 * feature caused it — so a saving added next quarter inherits the behaviour
 * without anyone writing a new effect for it:
 *
 *   ENTER   a saving that was not priced is priced now. The row fades in,
 *           washes green once, and its amount counts up from zero.
 *   CHANGE  a saving that was already there is a different number. The amount
 *           rolls old → new. No wash: a stepper re-prices on every click, and
 *           a cart that flashes on each one is a slot machine.
 *   EXIT    a saving is gone. The row collapses and the total counts back up.
 *           No wash, no colour — a loss is not celebrated.
 *
 * Two things move on *every* change whatever caused it: the discount pill and
 * the total. They are the spine — what teaches the customer that a moving pill
 * means a moved price.
 *
 * Nothing here holds React state. Every number is written straight to its own
 * text node inside `requestAnimationFrame`, because this app is on React 17:
 * a `setState` per frame per counter is a re-render of the whole cart per frame
 * per counter, with no automatic batching outside an event handler to save it.
 */

import React from "react";
import { formatCents } from "@homero/pricing";
import { useMediaQuery } from "../../hooks/useMediaQuery";

/** How long the total's tint takes to fade back to the cart's indigo. */
export const TINT_MS = 900;

/**
 * The total, tinting as it rolls — green when it drops, amber when it climbs.
 *
 * With the discount pill, the only thing that moves for *every* change there
 * is, which is what teaches the customer that a moving price is their doing.
 * One hook rather than one copy per surface: the cart and the sticky bar are
 * the same number in two places, and a rule with two implementations is a rule
 * that will be half-changed.
 *
 * The direction is kept in a ref and compared *inside the effect*, never
 * derived during render. Derived during render it becomes a changing class
 * name, which is a changing effect dependency, which re-runs the flash — so
 * the total would arrive already tinted on first paint and stay that way,
 * because the run that re-applied the class was not the run whose timer took
 * it off again.
 */
export function useRollingTotal(
  ref: React.RefObject<HTMLElement>,
  cents: number,
  enabled: boolean
): void {
  const previous = React.useRef(cents);
  const mounted = useHasMounted();

  React.useEffect(() => {
    const el = ref.current;
    const from = previous.current;
    previous.current = cents;

    if (!el || !mounted || !enabled || cents === from) return;

    const className = cents < from ? "rolling-down" : "rolling-up";
    el.classList.remove("rolling-down", "rolling-up");
    // Reading a layout property is what restarts the keyframes.
    void el.offsetWidth;
    el.classList.add(className);

    const timer = window.setTimeout(
      () => el.classList.remove(className),
      TINT_MS + 60
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cents, enabled, mounted]);
}

/** A saving as the cart itemises it. Mirrors the shape `Cart.tsx` builds. */
export type DiscountLine = { tag: string; label: string; cents: number };

/** A number that only moved. */
const COUNT_MS = 400;
/** A row leaving. Shorter than it arrived: there is nothing to read. */
const EXIT_MS = 240;
/** Between staggered arrivals, for the at-most-three that stagger. */
const STAGGER_MS = 90;
/** How long the green stays on the row that changed. */
const WASH_MS = 520;

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Whether this visitor has asked the system for less movement.
 *
 * Read live rather than once, because macOS and Windows both let it be turned
 * on mid-session and the cart is on screen for the length of a booking. Every
 * rolling figure calls this, so it goes through `useMediaQuery`'s shared
 * subscription rather than opening a `matchMedia` of its own.
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

/**
 * False for the first render, true after.
 *
 * The rule it exists for: never animate on mount. A customer stepping back to
 * the date should not watch their plan discount arrive a second time, and the
 * first paint of the cart is a statement of where the price already is.
 */
function useHasMounted(): boolean {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);
  return mounted;
}

/**
 * Put `className` on `ref`'s element for one animation, every time `token`
 * changes — restarting it if it is already running.
 *
 * The class has to come off and go back on with a reflow between, or a second
 * change inside the first animation's window is swallowed: the browser sees
 * the class already present and does not restart the keyframes.
 */
export function useFlash(
  ref: React.RefObject<HTMLElement>,
  token: string | number,
  className: string,
  enabled: boolean,
  /**
   * How long the class has to stay on for its animation to finish. Passed in
   * because the washes and the total's tint are different lengths, and a class
   * stripped early snaps the colour back mid-fade.
   */
  holdMs: number = WASH_MS
): void {
  const mounted = useHasMounted();
  React.useEffect(() => {
    const el = ref.current;
    if (!el || !mounted || !enabled) return;
    el.classList.remove(className);
    // Reading a layout property is what forces the restart.
    void el.offsetWidth;
    el.classList.add(className);
    const timer = window.setTimeout(() => el.classList.remove(className), holdMs + 60);
    return () => window.clearTimeout(timer);
    // `mounted` is deliberately a dependency: it flips once, and the flip must
    // not itself fire a flash for the value the cart opened on. `className` is
    // one too, because the total's flash picks its class from the direction the
    // number just moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, enabled, mounted, className, holdMs]);
}

type CountingProps = {
  /** Where the number is going. */
  value: number;
  /** Prints the number at each frame. */
  format: (value: number) => string;
  /** Printed in front of every frame, e.g. the minus on a saving. */
  prefix?: string;
  /**
   * The settled figure, published as an attribute.
   *
   * While a number rolls, its text is a frame of an animation and not the
   * price — anything reading it once can catch an intermediate value. React
   * writes this the instant the figure changes and it never passes through a
   * step in between, so it, not the text, is what a reader should trust.
   */
  "data-cents"?: number;
};

/**
 * A number that rolls to its new value instead of jumping.
 *
 * Renders the *final* value in its JSX, so the very first paint, a reduced
 * motion visitor and anything reading the DOM before a frame has run all see
 * the true figure rather than a zero. The roll is layered on top by writing
 * `textContent` — React never learns it happened, and the cart never re-renders
 * for it.
 */
function Counting({ value, format, prefix = "", ...rest }: CountingProps) {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  /** What the text node currently reads — the start of the next roll. */
  const shown = React.useRef<number>(value);
  const frame = React.useRef<number | null>(null);
  const reduced = usePrefersReducedMotion();
  const mounted = useHasMounted();

  /**
   * Layout, not passive.
   *
   * React has already written the *final* figure into this span by the time an
   * effect runs, and the roll then rewinds it to where the number came from.
   * On `useEffect` that rewind lands after the browser has painted, so every
   * change flashed its answer for one frame, snapped back, and only then
   * counted — the number appearing to move the wrong way first. Running before
   * paint means the only thing ever shown is the roll.
   */
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const from = shown.current;

    if (!mounted || reduced || from === value) {
      shown.current = value;
      el.textContent = prefix + format(value);
      return;
    }

    /**
     * Hold the old figure now, in this same commit.
     *
     * React has already written the destination into the span, and the first
     * frame of the roll is a `requestAnimationFrame` away — so without this
     * line there is always exactly one painted frame showing the answer before
     * the count to it begins. Writing the starting value here, before the
     * browser paints, is what makes the roll the only thing ever seen.
     */
    el.textContent = prefix + format(from);

    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / COUNT_MS);
      const at = Math.round(from + (value - from) * easeOutCubic(t));
      shown.current = at;
      el.textContent = prefix + format(at);
      frame.current = t < 1 ? requestAnimationFrame(step) : null;
    };
    frame.current = requestAnimationFrame(step);

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [value, prefix, format, reduced, mounted]);

  return (
    <span ref={ref} {...rest}>
      {prefix + format(value)}
    </span>
  );
}

/**
 * A cent amount that rolls. Named `cents` rather than `value` at the call site
 * so a reader of the cart can see the unit without opening this file — every
 * figure in this app is integer cents and nothing here may round.
 */
export function AnimatedCents({
  cents,
  ...rest
}: Omit<CountingProps, "format" | "value" | "data-cents"> & { cents: number }) {
  return <Counting {...rest} value={cents} data-cents={cents} format={formatCents} />;
}

const asPercent = (value: number) => `${value}%`;

/** The blended rate in the discount pill. Rolls for every change. */
export function AnimatedPercent(props: Omit<CountingProps, "format" | "data-cents">) {
  return <Counting {...props} format={asPercent} />;
}

/** A ledger row as it is rendered, which is not always as it is priced. */
type LedgerRow = DiscountLine & {
  /** Priced no longer, still on screen for one EXIT. */
  exiting: boolean;
  /** Arriving now, and should play the entrance. */
  entering: boolean;
  /** Its place in a staggered arrival. */
  delayMs: number;
};

/** What makes two sets of lines the same set, for diffing. */
function ledgerSignature(lines: DiscountLine[]): string {
  return lines.map((line) => `${line.tag}:${line.cents}:${line.label}`).join("|");
}

/**
 * The rows to render, which is the priced lines plus any that just stopped
 * being priced, held for one EXIT so they can collapse rather than vanish.
 *
 * Keyed on the signature rather than on the `lines` array, which is rebuilt
 * from the breakdown on every render and would otherwise re-enter this effect
 * forever.
 */
export function useLedgerRows(lines: DiscountLine[], reduced: boolean): LedgerRow[] {
  const signature = ledgerSignature(lines);
  const latest = React.useRef(lines);
  latest.current = lines;

  const mounted = useHasMounted();
  const [rows, setRows] = React.useState<LedgerRow[]>(() =>
    lines.map((line) => ({ ...line, exiting: false, entering: false, delayMs: 0 }))
  );

  React.useEffect(() => {
    const priced = latest.current;
    const animate = mounted && !reduced;

    setRows((current) => {
      const onScreen = new Set(
        current.filter((row) => !row.exiting).map((row) => row.tag)
      );
      const stillPriced = new Set(priced.map((line) => line.tag));

      // Rule: stagger at most three arrivals. Anything past the third lands
      // with the third, as one wash, rather than turning a date change into a
      // five-beat sequence nobody reads.
      let arrivals = 0;
      const next: LedgerRow[] = priced.map((line) => {
        const isNew = !onScreen.has(line.tag);
        return {
          ...line,
          exiting: false,
          entering: isNew && animate,
          delayMs: isNew && animate ? Math.min(arrivals++, 2) * STAGGER_MS : 0,
        };
      });

      if (animate) {
        // Put each departing row back where it was, so the list collapses from
        // the place the customer last saw the number.
        current.forEach((row, index) => {
          if (row.exiting || stillPriced.has(row.tag)) return;
          next.splice(Math.min(index, next.length), 0, {
            ...row,
            exiting: true,
            entering: false,
            delayMs: 0,
          });
        });
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, reduced, mounted]);

  // Drop the collapsed rows once their animation has run.
  const exitingKeys = rows
    .filter((row) => row.exiting)
    .map((row) => row.tag)
    .join("|");

  React.useEffect(() => {
    if (!exitingKeys) return;
    const timer = window.setTimeout(() => {
      setRows((current) => current.filter((row) => !row.exiting));
    }, EXIT_MS + 40);
    return () => window.clearTimeout(timer);
  }, [exitingKeys]);

  return rows;
}
